import type { AiReport } from "@/types";
import { AI_SEVERITY_CHIPS, AI_SEVERITY_LABELS } from "@/lib/walkaround";

interface AiFaultReportProps {
  report: AiReport;
  compact?: boolean;
}

function scoreTone(score: number): string {
  if (score >= 80) return "text-accent-300";
  if (score >= 55) return "text-primary-400";
  return "text-secondary-400";
}

export default function AiFaultReport({ report, compact = false }: AiFaultReportProps) {
  const faults = report.faults ?? [];
  const score = Math.round(report.condition_score ?? 0);

  return (
    <div className="rounded-xl border border-background-200/70 bg-background-100 p-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="flex items-center gap-2 text-sm font-medium text-foreground-950">
          <i className="ri-sparkling-2-line text-primary-500"></i> AI inspection
        </p>
        <div className="flex items-center gap-2">
          <span className="text-xs text-foreground-500">Condition</span>
          <span className={`font-heading text-lg font-semibold ${scoreTone(score)}`}>{score}/100</span>
        </div>
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-2">
        {report.damage_suspected ? (
          <span className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-full bg-secondary-100 px-2.5 py-1 text-[11px] font-medium text-secondary-900">
            <i className="ri-alert-line"></i> Possible damage spotted
          </span>
        ) : (
          <span className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-full bg-accent-100 px-2.5 py-1 text-[11px] font-medium text-accent-900">
            <i className="ri-shield-check-line"></i> No damage flagged
          </span>
        )}
        {report.model && (
          <span className="whitespace-nowrap rounded-full bg-background-200/60 px-2.5 py-1 text-[11px] text-foreground-600">
            {report.model}
          </span>
        )}
      </div>

      {report.summary && (
        <p className="mt-3 text-sm leading-relaxed text-foreground-700">{report.summary}</p>
      )}

      {faults.length > 0 && (
        <ul className="mt-3 space-y-2">
          {faults.map((fault, index) => (
            <li key={`${fault.label}-${index}`} className="rounded-lg bg-background-200/40 p-3">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <p className="text-sm font-medium text-foreground-950">{fault.label}</p>
                <span className={`inline-flex items-center whitespace-nowrap rounded-full px-2.5 py-0.5 text-[11px] font-medium ${AI_SEVERITY_CHIPS[fault.severity] ?? AI_SEVERITY_CHIPS.minor}`}>
                  {AI_SEVERITY_LABELS[fault.severity] ?? "Minor"}
                </span>
              </div>
              {!compact && (
                <p className="mt-1 text-xs leading-relaxed text-foreground-600">
                  {fault.area ? <span className="text-foreground-500">{fault.area} · </span> : null}
                  {fault.detail}
                </p>
              )}
            </li>
          ))}
        </ul>
      )}

      {faults.length === 0 && !report.damage_suspected && (
        <p className="mt-3 flex items-center gap-2 text-xs text-foreground-600">
          <i className="ri-checkbox-circle-line text-accent-400"></i> The model didn't spot any visible faults in these stills.
        </p>
      )}

      <p className="mt-3 flex items-start gap-1.5 text-[11px] leading-relaxed text-foreground-500">
        <i className="ri-information-line mt-0.5"></i>
        AI guidance only — always confirm with the clip before raising a dispute.
      </p>
    </div>
  );
}